import { forwardRef } from "react";
import { IMG_WIDTHS, imgSrcSet, imgUrl } from "@/lib/api";

/**
 * Responsive <img> for the house's photographs (served as pre-rendered WEBP variants by /api/images).
 *
 * - `width`   fallback variant for browsers without srcset support, and the only variant when no `sizes` is given.
 * - `sizes`   the rendered CSS width (media-query list). When present, every pre-rendered width is offered in a
 *             srcset so the browser fetches the smallest file that is still sharp on its screen.
 * - `priority` above the fold: eager, high fetch priority, synchronous decode. Everything else is lazy.
 */
export const Img = forwardRef(({ src, alt = "", width = 1200, widths = IMG_WIDTHS, sizes, priority = false, lazy, className, ...rest }, ref) => {
  if (!src) return null;
  const isLazy = lazy ?? !priority;
  return (
    <img
      ref={ref}
      src={imgUrl(src, width)}
      srcSet={sizes ? imgSrcSet(src, widths) : undefined}
      sizes={sizes}
      alt={alt}
      loading={isLazy ? "lazy" : "eager"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : isLazy ? "auto" : "low"}
      className={className}
      {...rest}
    />
  );
});
Img.displayName = "Img";

/* Common `sizes` recipes – the container is capped at 1440px (max-w-site) with 40px side padding on desktop. */
export const SIZES = {
  full: "(min-width: 1440px) 1360px, 100vw",
  half: "(min-width: 1440px) 680px, (min-width: 1024px) 50vw, 100vw",
  third: "(min-width: 1440px) 440px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
  twoThirds: "(min-width: 1440px) 900px, (min-width: 1024px) 60vw, 100vw",
};
